import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import jwtDecode from 'jwt-decode';
import storage from 'redux-persist/lib/storage';
import { ACCOUNT_TYPE, ALERT } from 'src/types/enum';
import { ErrorCodes } from 'src/utils/errorMappings';
import logger from 'src/utils/logger';
import { getFormattedAvailableTime } from 'src/utils/utils';
import { BASE_SERVER_URL } from '../../config';
import { enc } from 'crypto-js';
import { updateToken, kc as keycloak } from 'src/keycloak';
import { TRANSLATION_CONSTANTS as T } from 'src/utils/translations';
import kc, {
  getEncryptParams,
  getKcLang,
  getKCRealmName,
  getKcToken,
  getUserLogInStatus,
  isValidToken,
  setEncryptParams,
  setKccToken,
  setKcLang,
  setKCRealmName,
  setNewUser
} from '../../keycloak';
import { ILoggedInUser } from '../../types/user';
import axios from '../../utils/axios';
import {
  getChallenge,
  getChallengeHeader,
  getEncryptedToken,
  getPayload,
  KDF
} from '../../utils/encryption';
import type { AppThunk } from '../store';
import { setCoolDownModels } from './chat';
import { setCoolDownModels as setChatV2CoolDownModels } from './chatUi';
import { setApplicationResources } from './data';
import { setAnalysisCoolDownTime } from './docAnalyst';
import {
  setAnalyticsSalt,
  setNamespaceId,
  setSubscriptionInfo,
  setTenantInfo
} from './library';
import { setIsPaygActive } from './payg';
import { setAlert } from './snackbar';
import i18n from 'src/i18n/i18n';
import { setSmChatModel } from './smartMeet';

interface UserSettingsType {
  username: string;
  timezone: string;
  session_idle_time: string;
  ps_scheme?: number;
}

interface EncryptionParams {
  k: any;
  u: string;
  ka: any;
  mt: any;
  c: string;
}

interface AzureWebPubSubInfo {
  baseUrl: string;
  token: string;
  url: string;
}

interface AuthType {
  user?: ILoggedInUser | undefined;
  isAuthenticated: boolean;
  userDetails?: UserSettingsType;
  encryptionParams?: EncryptionParams;
  isAuthorized: boolean;
  azureWebPubSubInfo?: AzureWebPubSubInfo;
  licenseInfo?: LicenseInfo;
  maxAnalysisLimit?: number;
  assistantFileUploadLimit?: number;
  paygCreditsPerUnit?: number;
  isLicenseExpired: boolean;
}

interface LicenseInfo {
  creditLimit: string;
  paygCreditLimit: string;
  wordLimit: string;
}

const initialState: AuthType = {
  isAuthenticated: false,
  isAuthorized: false,
  isLicenseExpired: false
};

const isNonTrialUser = (accountType: string) =>
  accountType !== ACCOUNT_TYPE.TRIAL_ADMINISTRATOR &&
  accountType !== ACCOUNT_TYPE.TRIAL_USER;

const slice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    authSignIn(
      state: AuthType,
      action: PayloadAction<{
        user: ILoggedInUser;
        params: EncryptionParams;
        wsInfo: AzureWebPubSubInfo;
        licenseInfo: LicenseInfo;
        maxAnalysisLimit: number;
        assistantFileUploadLimit: number;
        paygCreditsPerUnit?: number;
      }>
    ) {
      const {
        user,
        params,
        wsInfo,
        licenseInfo,
        maxAnalysisLimit,
        assistantFileUploadLimit,
        paygCreditsPerUnit
      } = action.payload;
      state.user = user;
      state.isAuthenticated = true;
      state.encryptionParams = params;
      state.azureWebPubSubInfo = wsInfo;
      state.licenseInfo = licenseInfo;
      state.maxAnalysisLimit = maxAnalysisLimit;
      state.assistantFileUploadLimit = assistantFileUploadLimit;
      state.paygCreditsPerUnit = paygCreditsPerUnit;
    },
    authSignOut(state: AuthType) {
      state.user = undefined;
      state.isAuthenticated = false;
      state.encryptionParams = undefined;
      state.azureWebPubSubInfo = undefined;
      // this applies to all keys defined in persistConfig(s)
      storage.removeItem('persist:root');
      kc.logout({ redirectUri: kc.createLoginUrl() }).catch((err: any) => {
        alert('Error Logging Out!');
      });
    },
    userSettings(
      state: AuthType,
      action: PayloadAction<{ user: UserSettingsType }>
    ) {
      const { user } = action.payload;
      state.userDetails = user;
    },
    updateUserData(
      state: AuthType,
      action: PayloadAction<{ user: ILoggedInUser }>
    ) {
      const { user } = action.payload;
      state.user = user;
    },
    AuthorizedUser(
      state: AuthType,
      action: PayloadAction<{ isAuthorized: boolean }>
    ) {
      const { isAuthorized } = action.payload;
      state.isAuthorized = isAuthorized;
    },
    setLicenseExpired(
      state: AuthType,
      action: PayloadAction<{ isLicenseExpired: boolean }>
    ) {
      state.isLicenseExpired = action.payload.isLicenseExpired;
    },
    setCreditLimit(state: AuthType, action: PayloadAction<string>) {
      if (state.licenseInfo) {
        state.licenseInfo.creditLimit = action.payload;
      }
    },
    setPaygCreditLimit(state: AuthType, action: PayloadAction<string>) {
      if (state.licenseInfo) {
        state.licenseInfo.paygCreditLimit = action.payload;
      }
    }
  }
});

const endPoint: any = {
  register: 'update',
  unregister: 'unregister'
};
const serviceType = {
  typeRegister: 1,
  typeUnregister: 2
};

let tokenRegisterRetries = 0; // retry counter for update token

export const reducer = slice.reducer;

// Register User
export const authSignIn =
  (): AppThunk => async (dispatch: any, getState: any, token: any) => {
    tokenRegisterRetries = 0;
    sessionRequest(
      endPoint.register,
      serviceType.typeRegister,
      dispatch,
      getState,
      token
    );
  };

// Unregister User
export const authSignOut =
  (): AppThunk => async (dispatch: any, getState: any) => {
    sessionRequest(
      endPoint.unregister,
      serviceType.typeUnregister,
      dispatch,
      getState
    );
  };

let hasShownTokenAlert = false;
function sessionRequest(
  endpoint: string,
  type: number,
  dispatch: any,
  getState: any,
  token?: any
) {
  try {
    const accessToken = getKcToken();
    if (accessToken && isValidToken(accessToken)) {
      switch (type) {
        case serviceType.typeRegister:
          const response: any = jwtDecode(accessToken);
          let formDataUpdate = new FormData();
          const key_db64 = enc.Utf8.stringify(
            enc.Base64.parse(import.meta.env.REACT_APP_ENCRPT_KEY_B64 || '')
          );
          let params = KDF(response, accessToken, key_db64);
          let encrypted_token = getEncryptedToken(params, accessToken);
          let challenge = getChallenge(params, null);
          let isLogin = getUserLogInStatus() === 'true' ? 'false' : 'true';
          formDataUpdate.append('payload', challenge);
          formDataUpdate.append('isLogin', isLogin);
          axios
            .post(
              `${BASE_SERVER_URL}/api/v1/session/${endpoint}`,
              formDataUpdate,
              {
                headers: {
                  Authorization: `${encrypted_token}`,
                  withCredentials: 'true'
                }
              }
            )
            .then((sessionRes: any) => {
              if (sessionRes.status === 401) {
                dispatch(
                  slice.actions.AuthorizedUser({
                    isAuthorized: false
                  })
                );
              } else if (sessionRes.status === 403) {
                dispatch(
                  slice.actions.AuthorizedUser({
                    isAuthorized: false
                  })
                );
                alert(
                  'Error: You have not been invited to EmpowerGPT. Please request invitation to join.'
                );
                logoutSession(dispatch);
                return;
              } else {
                dispatch(
                  slice.actions.AuthorizedUser({
                    isAuthorized: true
                  })
                );
              }
              let user = {
                id: response.sub || '',
                name: response.name || response.preferred_username || '',
                email: response.email || '',
                username: response.preferred_username || '',
                roles: sessionRes?.data?.ret_val?.content_roles || [],
                user_roles: sessionRes?.data?.ret_val?.user_roles || [],
                cost_centre: response.cost_centre || '',
                title:
                  response.title ||
                  sessionRes?.data?.ret_val?.user_attributes_info
                    ?.job_title?.[0] ||
                  '',
                department:
                  response.department ||
                  sessionRes?.data?.ret_val?.user_attributes_info
                    ?.department?.[0] ||
                  '',
                companyName:
                  sessionRes?.data?.ret_val?.user_attributes_info
                    ?.company_name?.[0] || '',
                country:
                  sessionRes?.data?.ret_val?.user_attributes_info
                    ?.country?.[0] || '',
                manager: response.manager || '',
                area: response.area || '',
                accountType:
                  response.account_type ||
                  sessionRes?.data?.ret_val?.account_type ||
                  '',
                userType: response.user_type || '',
                supportEmail: sessionRes?.data?.ret_val?.support_email,
                isNonTrialUser: isNonTrialUser(
                  response.account_type ||
                    sessionRes?.data?.ret_val?.account_type ||
                    ''
                )
              };
              let applicationResources = {
                ...sessionRes?.data?.ret_val?.types
              };

              // setting new User check if user has no roles
              if (user.roles.length === 0) {
                setNewUser('true');
              } else {
                setNewUser('false');
              }
              // setting encryption params in localStorage
              let tempEncryptParams = {
                ...params,
                c: sessionRes?.data?.ret_val?.cors_uuid || null
              };
              setEncryptParams(tempEncryptParams);
              dispatch(
                slice.actions.authSignIn({
                  user: user,
                  params: {
                    ...params,
                    c: sessionRes?.data?.ret_val?.cors_uuid || null
                  },
                  wsInfo: sessionRes?.data?.ret_val?.ws_info || null,
                  licenseInfo: {
                    creditLimit:
                      sessionRes?.data?.ret_val?.credit_limit_info
                        ?.consumed_license_info?.credit_limit?.code?.[1] || '',
                    paygCreditLimit:
                      sessionRes?.data?.ret_val?.credit_limit_info
                        ?.consumed_license_info?.payg_credit_limit?.code?.[1] ||
                      '',
                    wordLimit:
                      sessionRes?.data?.ret_val?.credit_limit_info
                        ?.consumed_license_info?.word_limit?.code?.[1] || ''
                  },
                  maxAnalysisLimit:
                    sessionRes?.data?.ret_val?.app_configuration
                      ?.da_max_running_analysis || 1,
                  assistantFileUploadLimit:
                    sessionRes?.data?.ret_val?.app_configuration
                      ?.assistant_file_upload_limit || 1000,
                  paygCreditsPerUnit:
                    sessionRes?.data?.ret_val?.app_configuration
                      ?.payg_credits_per_unit || null
                })
              );
              const { models: modelRateLimits, mini_expert } =
                sessionRes?.data?.ret_val?.user_rate_limits;
              if (modelRateLimits?.length) {
                let coolDownModels = {};
                modelRateLimits.forEach((model) => {
                  const {
                    model_id: modelId,
                    restriction_time,
                    tier_name: modelTier
                  } = model;
                  const { availableAt, availableTime } =
                    getFormattedAvailableTime(restriction_time);
                  coolDownModels[model.model_id] = {
                    availableTime,
                    availableAt,
                    modelTier
                  };
                });
                dispatch(setCoolDownModels({ coolDownModels: coolDownModels }));
                dispatch(setChatV2CoolDownModels(coolDownModels));
              }
              if (mini_expert?.restricted_time) {
                const { availableAt, availableTime } =
                  getFormattedAvailableTime(mini_expert.restricted_time);
                dispatch(
                  setAnalysisCoolDownTime({ availableAt, availableTime })
                );
              }

              dispatch(
                setApplicationResources({
                  applicationResources: applicationResources
                })
              );
              dispatch(
                setNamespaceId(sessionRes?.data?.ret_val?.types?.Namespace)
              );
              dispatch(setTenantInfo(sessionRes?.data?.ret_val?.tenant_info));
              dispatch(
                setSubscriptionInfo(
                  sessionRes?.data?.ret_val?.subscription_info
                )
              );
              dispatch(
                setIsPaygActive(
                  Boolean(
                    sessionRes?.data?.ret_val?.credit_limit_info
                      ?.consumed_license_info?.is_payg_active
                  )
                )
              );
              if (sessionRes?.data?.ret_val?.analytics_salt) {
                dispatch(
                  setAnalyticsSalt(sessionRes?.data?.ret_val?.analytics_salt)
                );
              }

              const smChatModel =
                sessionRes?.data?.ret_val?.app_configuration
                  ?.smart_meet_chat_model;

              if (smChatModel) {
                dispatch(setSmChatModel(smChatModel));
              }
              setKccToken('false');
            })
            .catch((err: any) => {
              // add 3 retries to register
              logger.log('Error on Update', err);
              tokenRegisterRetries++;
              if (tokenRegisterRetries < 3) {
                sessionRequest(
                  endPoint?.register,
                  type,
                  dispatch,
                  getState,
                  token
                );
                logger.log('retrying Count', tokenRegisterRetries);
              } else {
                alert('Error: Updating session, Please try again!');
                logoutSession(dispatch);
              }
            });
          break;

        default:
          const encryptionParams = getEncryptParams();
          const uuid = getPayload(encryptionParams?.u, encryptionParams);
          let formData = new FormData();
          formData.append('payload', JSON.stringify(uuid));
          axios
            .post(`${BASE_SERVER_URL}/api/v1/session/${endpoint}`, formData, {
              headers: {
                Channel: getChallengeHeader(
                  encryptionParams,
                  `session/${endpoint}`,
                  formData
                )
              }
            })
            .then(() => {
              logoutSession(dispatch);
            })
            .catch((err: any) => {
              alert(`Error logging out user! ${err}`);
            });
          break;
      }
    } else {
      logger.log('Token Invalid!');
      if (!hasShownTokenAlert) {
        hasShownTokenAlert = true;
        updateToken(keycloak).then((success) => {
          hasShownTokenAlert = false;
          if (!success) {
            dispatch(
              setAlert({
                msg: i18n.t(T['invalidToken']),
                type: ALERT.WARNING
              })
            );

            setTimeout(() => {
              logoutSession(dispatch);
            }, 3000);
          } else {
            sessionRequest(endpoint, type, dispatch, getState, token);
          }
        });
      }
    }
  } catch (err) {
    logoutSession(dispatch);
  }
}

function logoutSession(dispatch: any) {
  const kcRealmName = getKCRealmName();
  const kcLang = getKcLang();
  localStorage.clear();
  dispatch(slice.actions.authSignOut());
  if (kcRealmName) {
    setKCRealmName(kcRealmName);
  }
  if (kcLang) {
    setKcLang(kcLang);
  }
}

export const userSettings =
  (userDetails: UserSettingsType): AppThunk =>
  async (dispatch: any) => {
    dispatch(
      slice.actions.userSettings({
        user: userDetails
      })
    );
  };

export const AuthorizedUser =
  (isAuthorized: boolean): AppThunk =>
  async (dispatch: any) => {
    dispatch(
      slice.actions.AuthorizedUser({
        isAuthorized: isAuthorized
      })
    );
  };
// axios.interceptors.request.use((request) => {
//   logger.log("Starting Request", JSON.stringify(request, null, 2));
//   return request;
// });

// axios.interceptors.response.use((response) => {
//   logger.log("Response:", JSON.stringify(response, null, 2));
//   return response;
// });

export default slice;

export const {
  updateUserData,
  setLicenseExpired,
  setCreditLimit,
  setPaygCreditLimit
} = slice.actions;

// Credit limit selectors — single source of truth for threshold checks
export const selectCreditLimitReached = (state: {
  auth: { licenseInfo?: { creditLimit: string } };
}): boolean =>
  state.auth.licenseInfo?.creditLimit ===
  ErrorCodes.CREDIT_LIMIT_REACHED_100_PERCENT;

export const selectCreditWarningReached = (state: {
  auth: { licenseInfo?: { creditLimit: string } };
}): boolean =>
  state.auth.licenseInfo?.creditLimit ===
  ErrorCodes.CREDIT_LIMIT_EXCEEDED_90_PERCENT;

export const selectPaygCreditLimitReached = (state: {
  auth: { licenseInfo?: { paygCreditLimit: string } };
}): boolean =>
  state.auth.licenseInfo?.paygCreditLimit ===
  ErrorCodes.PAYG_CREDIT_LIMIT_REACHED_100_PERCENT;

export const selectPaygCreditWarningReached = (state: {
  auth: { licenseInfo?: { paygCreditLimit: string } };
}): boolean =>
  state.auth.licenseInfo?.paygCreditLimit ===
  ErrorCodes.PAYG_CREDIT_LIMIT_EXCEEDED_90_PERCENT;
